import React, { useEffect, useMemo, useState } from 'react';
import TemplateThumbnailsContainer from './TemplateThumbnailsContainer';
import TemplateContentLoader from './TemplateContentLoader';
import TemplatePromptInput from './TemplatePromptInput';
import DocViewer, { DocViewerRenderers } from 'react-doc-viewer';
import { useDispatch } from 'react-redux';
import { MotifButton, MotifPagination } from '@ey-xd/motif-react';

const ReportEditTemplate = (props: any) => {
  const { pptURL, uniqueReportCacheId, engagementId, totalSlides, reportId, reportStatus } = props;

  const [selectedSlide, setSelectedSlide] = useState<number>(1);
  const dispatch = useDispatch();

  const getReportDetails = () => {
    dispatch({
      type: 'WORKSPACE_ADDED_REPORT_DETAILS',
      payload: reportId
    });
  };

  // Load report details on mount or reportId change
  useEffect(() => {
    if (reportId) {
      getReportDetails();
    }
  }, [reportId]);

  // Load content for selected slide
  useEffect(() => {
    if (reportStatus === 'Processed' && uniqueReportCacheId && selectedSlide) {
      dispatch({
        type: 'GET_CONTENT_FOR_SLIDE_IN_REPORT',
        payload: {
          slideNumber: `${selectedSlide}`,
          uniqueReportCacheId
        }
      });
    }
  }, [dispatch, reportStatus, uniqueReportCacheId, selectedSlide]);

  // Ensure selectedSlide is within totalSlides range
  useEffect(() => {
    if (totalSlides && selectedSlide > totalSlides) {
      setSelectedSlide(totalSlides);
    }
  }, [totalSlides, selectedSlide]);

  // Memoized DocViewer component
  const memoizedSlideViewer = useMemo(() => {
    return pptURL ? (
      <DocViewer
        style={{ height: '60vh', width: '100%' }}
        pluginRenderers={DocViewerRenderers}
        documents={[{ uri: pptURL, fileType: 'pptx' }]}
      />
    ) : (
      <p>Loading ...</p>
    );
  }, [pptURL]);

  if (reportStatus !== 'Processed') {
    return (
      <div className="flex gap-2 justify-between items-center">
        <p className="text-lg truncate">
          RFP response is being generated. Please click 'Refresh' to check the latest status.
        </p>
        <MotifButton size="small" variant="primary" onClick={getReportDetails}>
          Refresh
        </MotifButton>
      </div>
    );
  }

  return (
    <div className="flex gap-3 flex-col">
      <div className="flex justify-between">
        {/* Thumbnail viewer can be added back if needed */}
        {/* <div className="w-[24.5%] h-full">
          <TemplateThumbnailsContainer selectedSlide={selectedSlide} onSlideSelect={setSelectedSlide} />
        </div> */}
        <div className="w-[49.5%] h-full">
          <div className="flex flex-col gap-3 h-full">
            <div>{memoizedSlideViewer}</div>
          </div>
        </div>
        <div className="w-[49.5%]">
          <div className="flex flex-col gap-3">
            <TemplateContentLoader uniqueReportCacheId={uniqueReportCacheId} />
            {totalSlides && (
              <div className="flex justify-around">
                <MotifPagination
                  allowInput
                  aria-label="Pagination Navigation"
                  currentPage={selectedSlide}
                  firstButtonProps={{ title: 'Go to first page' }}
                  lastButtonProps={{ title: 'Go to last page' }}
                  max={totalSlides}
                  min={1}
                  nextButtonProps={{ title: 'Go to next page' }}
                  onPageChange={(page) => {
                    if (page !== selectedSlide) {
                      setSelectedSlide(page as number);
                    }
                  }}
                  prevButtonProps={{ title: 'Go to previous page' }}
                  role="navigation"
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <TemplatePromptInput
        uniqueReportCacheId={uniqueReportCacheId}
        engagementId={engagementId}
        currentPage={selectedSlide}
      />
    </div>
  );
};

export default ReportEditTemplate;
